import type { PublicCustomField } from './property-custom-fields'

/**
 * Name resolution for operator-imported custom fields (dashboard#1420).
 * Operators type field names by hand, so comparison is lowercased with every
 * non-alphanumeric character stripped (`Bedroom 1 Image`, `bedroom1-image`
 * and `bedroom1_image` all land on the same bucket) — the same idea as the
 * toolkit's own key normalizer (`homerunner-toolkit`
 * `includes/ExternalCustomFields/Syncer.php`, `sanitize_key()` around line
 * 425), but NOT the same implementation: the toolkit's is ASCII-only
 * (`mb_strtolower` + `preg_replace('/[^a-z0-9]+/', '_', ...)`), while this
 * one is Unicode-aware and additionally folds Latin/Cyrillic/Greek
 * diacritics (dashboard#1520). A non-ASCII field name (or a Latin one with
 * accents) therefore resolves HERE and stays unresolved on WordPress — the
 * two sides agree only on plain-ASCII names.
 */
export function normalizeCustomFieldName(name: string): string {
  // Unicode-aware, not `[^a-z0-9]`. Central publishes both the operator's
  // original `name` and a `Str::slug` transliteration of it, and the settings
  // panel tells the operator to type "the exact custom field name as
  // imported". With an ASCII-only class a non-Latin name normalized to '' and
  // resolved nothing, silently — only the transliterated slug they never see
  // would have matched (`Площадь` -> `ploshhad`). NFKD + a LATIN-ONLY
  // combining-diacritics strip (U+0300-U+036F, the block NFKD decomposes
  // Latin/Cyrillic/Greek accents into) lets `Wohnfläche` and `Wohnflache`
  // land on the same bucket, which is what an operator retyping a name by
  // hand actually does — without also stripping combining marks OUTSIDE that
  // block, which are not diacritics on a base letter but distinguish the
  // letter itself (dashboard#1520): Japanese ば/は decompose under NFKD to
  // は + U+3099 (combining voiced sound mark) vs は alone, and Devanagari
  // कि/का share base U+0915 with different combining vowel signs
  // (U+093F/U+093E) — stripping every `\p{M}` collapsed both pairs onto one
  // key.
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]+/g, '')
    .toLowerCase()
    // `\p{M}` (not just `\p{L}\p{N}`): a combining mark that survived the
    // strip above is one we deliberately kept (it distinguishes the base
    // letter rather than accenting it) — the old blanket `[^\p{L}\p{N}]`
    // filter would silently re-strip it here and reopen the same collision.
    .replace(/[^\p{L}\p{N}\p{M}]/gu, '')
}

/**
 * A custom field's `name`/`value` as renderable text. The payload is opaque
 * JSON typed after the fact, so a null, a number or an object can arrive where
 * a string is declared; an object renders as nothing rather than crashing
 * React or the SSR pass.
 */
export function customFieldText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/**
 * Index a payload's custom fields by normalized name AND slug for O(1)
 * lookup. First definition wins on a collision — a later duplicate name
 * must not repoint an already-resolved field (same rule as the toolkit).
 */
export function indexCustomFields(fields: PublicCustomField[]): Map<string, PublicCustomField> {
  const index = new Map<string, PublicCustomField>()
  for (const field of fields) {
    for (const raw of [field.name, field.slug]) {
      if (!raw) continue
      const key = normalizeCustomFieldName(raw)
      if (key && !index.has(key)) index.set(key, field)
    }
  }
  return index
}

/** One operator-configured field to surface, with an optional display-label override. */
export type CustomFieldMappingEntry = {
  // Nullable: Central's ConvertEmptyStringsToNull stores a blank one as null.
  fieldName?: string | null
  label?: string | null
}

export type CustomFieldsMapping = {
  enabled?: boolean | null
  position?: 'top' | 'bottom' | null
  fields?: CustomFieldMappingEntry[] | null
}

export type ResolvedCustomField = {
  fieldName: string
  label: string
  value: string
}

/**
 * Resolve the operator's field mapping against a property's actual custom
 * fields. Entries with no match (field not imported for this property, or
 * imported blank) are dropped rather than rendered empty.
 */
export function resolveCustomFields(
  mapping: CustomFieldsMapping | null | undefined,
  fields: PublicCustomField[] | null | undefined,
): ResolvedCustomField[] {
  if (!mapping?.enabled || !mapping.fields?.length || !fields?.length) return []

  const index = indexCustomFields(fields)
  const resolved: ResolvedCustomField[] = []

  // Two mapping entries can normalize to the SAME imported field ("Pool Type"
  // and "pool_type", or the same name typed twice). Without this the block
  // rendered the row twice AND handed React duplicate keys.
  const seen = new Set<string>()

  for (const entry of mapping.fields) {
    const key = normalizeCustomFieldName(entry?.fieldName ?? '')
    if (!key || seen.has(key)) continue
    const field = index.get(key)
    if (!field) continue
    // Coerced, not asserted: `PublicCustomField` is a TypeScript annotation on
    // an opaque JSON payload, not a runtime guarantee. A null or numeric
    // `value` reaching `.trim()` threw inside renderToString on the SSR
    // property route — past the per-widget catch, so the whole page 500'd
    // rather than degrading (Codex, 2026-09-14).
    const value = customFieldText(field.value)
    if (value === '') continue
    seen.add(key)
    resolved.push({
      // The operator's own spelling, unchanged — `seen` already guarantees one
      // resolved entry per underlying field, so these stay unique.
      fieldName: entry.fieldName ?? key,
      label: entry.label?.trim() ? entry.label.trim() : customFieldText(field.name),
      value,
    })
  }

  return resolved
}
